import React, { useEffect, useState } from "react";
import {
  createItem,
  deleteItem,
  fetchItems,
  updateItem,
} from "../api/ItemService";
import { ItemType } from "../models/ItemTypes";
import { fetchItemModelData } from "../api/ItemModelService";
import { fetchItemCategoryData } from "../api/ItemCategoryService";
import { fetchUomData } from "../api/uomService";
import { ItemModel } from "../models/ItemModelTypes";
import { ItemCategory } from "../models/ItemCategoryTypes";
import { UOM } from "../models/uomtypes";

import AddItemForm from "../forms/AddItemForm";
import { Key, Plus, X } from "lucide-react";
import CustomAlert from "../view/common/alert/CustomAlert";

import Pagination from "../view/common/pagination/Pagination";
import TableComponent from "./TableComponent";
import "../styles/common/sub-menu.css";
import "../styles/common/spinner.css";
import "../styles/common/icon-style.css";
import "../styles/ItemModels.css";
import "../styles/common/toplayout.css";
import "../styles/common/btn.css";
import "../styles/common/custom-alert.css";

import "../styles/table.css"

import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../redux/slices/store";
import { resetFilters, updateFilters } from "../redux/slices/filterSlice";
import { kebabCase } from "lodash";

const ItemList: React.FC = () => {
  const [items, setItems] = useState<ItemType[]>([]);
  const [itemModels, setItemModels] = useState<ItemModel[]>([]);
  const [categories, setCategories] = useState<ItemCategory[]>([]);
  const [uoms, setUoms] = useState<UOM[]>([]);
  const [isAdding, setIsAdding] = useState(false);
  const [editingItem, setEditingItem] = useState<ItemType | undefined>(
    undefined
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit] = useState(10);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [menuTitle, setMenuTitle] = useState<string>("Items");
  
  const [sortBy, setSortBy] = useState<string>("partName");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertConfirmAction, setAlertConfirmAction] = useState<() => void>(
    () => {}
  );
  const [alertCancelAction, setAlertCancelAction] = useState<() => void>(
    () => {}
  );
  const [showConfirmButton, setShowConfirmButton] = useState(true);
  const [showCancelButton, setShowCancelButton] = useState(true);
  const [confirmButtonText, setConfirmButtonText] = useState("OK");
  const [cancelButtonText, setCancelButtonText] = useState("Cancel");
  const [autoCloseDelay, setAutoCloseDelay] = useState<number | null>(null);

  const dispatch = useDispatch();
  const filters = useSelector((state: RootState) => state.filters);
  const  permission = useSelector((state:RootState) => state.permissions)
   const [initializeItemList, isItemListInitialized] = useState(false);

  useEffect(()=>{
    dispatch(resetFilters());
    isItemListInitialized(true);
  },[dispatch]); 
 useEffect(() => {
    const breadcrumbElements = document.querySelectorAll('.breadcrumb-nav span');
    if (breadcrumbElements.length >= 2) {
      const menuTitleElement = breadcrumbElements[breadcrumbElements.length - 1];
      setMenuTitle(menuTitleElement.textContent || "Items");
    }
  }, []);
  useEffect(() => {
    if(initializeItemList){
       loadItems();
  }}, [initializeItemList,searchTerm, currentPage, itemsPerPage, filters, sortBy, sortOrder]);

  const loadItems = async () => {
    setIsLoading(true);
    try {
      const result: any = await fetchItems({
        page: currentPage,
        limit: itemsPerPage,
        search: searchTerm,
        filter: filters,
        sortBy: sortBy,
        sortOrder: sortOrder,
      });

      if (!result.error && result.data) {
        setItems(result.data.data || []);
        setTotalPages(result.data.pagination.totalPages);
        setTotalItems(result.data.pagination.total || 0);
      } else {
        setItems([]);
      }
    } catch (err) {
      setError("Failed to load items");
    }
    setIsLoading(false);
  };

  const handleSortChange = (
    sortByField: string,
    sortOrderDirection: "asc" | "desc"
  ) => {
    setSortBy(sortByField);
    setSortOrder(sortOrderDirection);
  };

 
  const closeAlert = () => {
    setShowAlert(false);
  };

 
  const configureAlert = (
    message: string,
    confirmAction: () => void,
    cancelAction: () => void = () => {},
    confirmText: string = "OK",
    cancelText: string = "Cancel",
    showConfirm: boolean = true,
    showCancel: boolean = true,
    autoClose: number | null = null
  ) => {
    setAlertMessage(message);
    setAlertConfirmAction(() => confirmAction);
    setAlertCancelAction(() => cancelAction);
    setConfirmButtonText(confirmText);
    setCancelButtonText(cancelText);
    setShowConfirmButton(showConfirm);
    setShowCancelButton(showCancel);
    setAutoCloseDelay(autoClose);
    setShowAlert(true);
  };
  const handleSaveItem = async (item: ItemType) => {
    try {
      let updatedItem = { ...item };

      
      const getObjectId = (obj: any) => {
        return typeof obj === "object" && obj !== null ? obj._id : obj;
      };

      if (editingItem) {
       
        const hasChanges =
          !editingItem ||
          editingItem.hsnCode !== item.hsnCode ||
          editingItem.partName !== item.partName ||
          editingItem.internalPartNo !== item.internalPartNo ||
          editingItem.externalPartNo !== item.externalPartNo ||
          getObjectId(editingItem.model) !== getObjectId(item.model) ||
          getObjectId(editingItem.category) !== getObjectId(item.category) ||
          getObjectId(editingItem.uom) !== getObjectId(item.uom) ||
          editingItem.purchaseItem !== item.purchaseItem ||
          editingItem.sellingItem !== item.sellingItem ||
          editingItem.productionItem !== item.productionItem ||
          editingItem.stockPerDay !== item.stockPerDay ||
          editingItem.packingQty !== item.packingQty ||
          editingItem.safetyDays !== item.safetyDays ||
          editingItem.minReqDay !== item.minReqDay ||
          editingItem.maxReqDays !== item.maxReqDays ||
          editingItem.selfLife !== item.selfLife ||
          editingItem.expiryDays !== item.expiryDays ||
          editingItem.scheduleNo !== item.scheduleNo ||
          editingItem.status !== item.status;

        if (!hasChanges) {
         
          configureAlert(
            "No changes were made.",
            closeAlert,
            closeAlert,
            "OK",
            "",
            true,
            false,
            3000
          );
          return;
        }

        if (editingItem?.status === "INACTIVE") {
          updatedItem.status = "ACTIVE";
        }
        const response: any = await updateItem(
          editingItem._id || "",
          updatedItem
        );
        setEditingItem(undefined);

        if (response.data) {
          configureAlert(
            `${item.partName} has been Updated successfully.`,
            () => {
              setIsAdding(false);
              loadItems();
              closeAlert();
            },
            closeAlert,
            "OK",
            "",
            true,
            false,
            3000
          );
        } else {
          configureAlert(
            `Duplicate entry!`,
            () => {
              closeAlert();
            },
            closeAlert,
            "OK",
            "",
            true,
            false,
            3000
          );
        }
      } else {
        const response: any = await createItem(updatedItem);

        if (response.data) {
          configureAlert(
            `${item.partName} has been added successfully.`,
            () => {
              setIsAdding(false);
              loadItems();
              closeAlert();
            },
            closeAlert,
            "OK",
            "",
            true,
            false,
            3000
          );
        } else {
          configureAlert(
            `Duplicate entry!`,
            () => {
              closeAlert();
            },
            closeAlert,
            "OK",
            "",
            true,
            false,
            3000
          );
        }
      }
    } catch (error) {
      console.error("Error saving item:", error);
      configureAlert(
        `Failed to ${editingItem ? "update" : "add"} item.`,
        closeAlert,
        closeAlert,
        "OK",
        "",
        true,
        false,
        3000
      );
    }
  };

  const handleEdit = (item: ItemType) => {
    setEditingItem(item);
    setIsAdding(true);
  };

  const handleDeleteItem = async (id: string) => {
    const itemToDelete = items.find((item) => item._id === id);
    if (itemToDelete) {
      configureAlert(
        `Are you sure you want to Inactive ${itemToDelete.partName}?`,
        async () => {
          closeAlert();
          try {
            await deleteItem(id);
            await loadItems();

            configureAlert(
              `Item has been Inactive successfully.`,
              () => {
                closeAlert();
              },
              closeAlert,
              "OK",
              "",
              true,
              false,
              3000
            );
          } catch (error) {
            console.error("Error deleting item:", error);
            configureAlert(
              "Failed to delete item.",
              closeAlert,
              closeAlert,
              "OK",
              "",
              true,
              false,
              3000
            );
          }
        },
        closeAlert,
        "Yes",
        "No",
        true,
        true,
        null
      );
    }
  };

  const handleClosePopup = () => {
    if (
      isAdding &&
      (editingItem || document.querySelector("form")?.checkValidity() === false)
    ) {
      configureAlert(
        "Are you sure you want to close without saving?",
        () => {
          setIsAdding(false);
          setEditingItem(undefined);
          closeAlert();
        },
        closeAlert,
        "Yes",
        "No",
        true,
        true,
        null
      );
    } else {
      setIsAdding(false);
      setEditingItem(undefined);
    }
  };

  const columns = [
    { key: "hsnCode", label: "HSN Code", sortable: true },
    { key: "partName", label: "Part Name", sortable: true },
    { key: "internalPartNo", label: "Internal Part No", sortable: true },
    { key: "externalPartNo", label: "External Part No", sortable: true },
    {
      key: "model",
      label: "Model",
      nameKey: "name",
      render: (value: any) => (typeof value === "object" ? value?.name : "N/A"),
      sortable: true,
    },
    {
      key: "category",
      label: "Category",
      nameKey: "name",
      render: (value: any) => (typeof value === "object" ? value?.name : "N/A"),
      sortable: true,
    },
    {
      key: "uom",
      label: "UOM",
      nameKey: "name",
      render: (value: any) => (typeof value === "object" ? value?.name : "N/A"),
      sortable: true,
    },
    {
      key: "status",
      label: "Status",
      sortable: true,
    },
  ];

  return (
    <div className="item-models-container">
      <div className="item-models-header">
        <div className="border">
          <div className="start-end-layout">
            <div className="breadcrumb-nav">
              <span>Master Item</span>
              <span>Item List</span>
            </div>
            
            {permission.menus.items.create ? 
            <div
              className="items-button-container tooltip-wrapper"
              onClick={() => setIsAdding(true)}
            >
              <button className="item-models-add-btn">
                <Plus size={16} />
              </button>
            </div> : <></> }
          </div>
        </div>
      </div>

      <div className="box-container">
      {isLoading ? (
        <div className="spinner"></div>
      ) : (
        <>
          <TableComponent
            columns={columns}
            data={items}
            onEdit={handleEdit}
            onDelete={handleDeleteItem}
            setFilter={(filters: Record<string, any>) => {
              Object.entries(filters).forEach(([key, value]) => {
                dispatch(updateFilters({ key, value: String(value) }));
              });
            }}
            setSorting={handleSortChange}
            defaultSortConfig={{ key: sortBy, direction: sortOrder }}
            excludeFields={["scheduleNo"]}
            menuTitle={menuTitle}
          />
          
          <div className="pagination-container">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              itemsPerPage={itemsPerPage}
              setItemsPerPage={setItemsPerPage}
              totalItems={totalItems}
            />
          </div>
        </>
      )}
    </div>

      {isAdding && (
        <div className="popup-overlay">
          <div className="popup-container">
            <div className="popup-header">
              <h2>{editingItem ? "Edit Item" : "Add New Item"}</h2>
              <button className="close-button" onClick={handleClosePopup}>
                <X size={16} />
              </button>
            </div>
            <div className="popup-content">
              <AddItemForm
                onSave={handleSaveItem}
                onCancel={handleClosePopup}
                isEditing={!!editingItem}
                editingItem={editingItem}
              />
            </div>
          </div>
        </div>
      )}

      {showAlert && (
        <CustomAlert
          message={alertMessage}
          onConfirm={alertConfirmAction}
          onCancel={alertCancelAction}
          confirmText={confirmButtonText}
          cancelText={cancelButtonText}
          showConfirmButton={showConfirmButton}
          showCancelButton={showCancelButton}
          autoCloseDelay={autoCloseDelay}
        />
      )}
    </div>
  );
};

export default ItemList;